import { z } from 'zod'

export const assistantTaskTypeSchema = z.enum(['voiceover_video'])

export const assistantAspectRatioSchema = z.enum(['9:16', '16:9', '1:1'])

export const assistantToneSchema = z.enum([
  'natural',
  'professional',
  'friendly',
  'energetic',
  'calm',
])

export const voiceoverVideoPlanSchema = z
  .object({
    taskType: z.literal('voiceover_video'),
    topic: z.string().trim().min(1),
    script: z.string().trim().min(1),
    durationSeconds: z.number().int().min(5).max(600),
    aspectRatio: assistantAspectRatioSchema,
    tone: assistantToneSchema,
    needsTts: z.boolean(),
    needsCaptions: z.boolean(),
    needsBgm: z.boolean(),
    exportDraft: z.boolean(),
  })
  .strict()

export type AssistantTaskType = z.infer<typeof assistantTaskTypeSchema>
export type AssistantAspectRatio = z.infer<typeof assistantAspectRatioSchema>
export type AssistantTone = z.infer<typeof assistantToneSchema>
export type VoiceoverVideoPlan = z.infer<typeof voiceoverVideoPlanSchema>

function sanitizeJsonBlock(rawText: string): string {
  const trimmed = rawText.trim()
  const fencedMatch = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i)
  if (fencedMatch?.[1]) {
    return fencedMatch[1].trim()
  }

  const firstBrace = trimmed.indexOf('{')
  const lastBrace = trimmed.lastIndexOf('}')
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    return trimmed.slice(firstBrace, lastBrace + 1)
  }

  return trimmed
}

export function parseVoiceoverVideoPlan(rawText: string): VoiceoverVideoPlan {
  const normalized = sanitizeJsonBlock(rawText)

  let parsed: unknown
  try {
    parsed = JSON.parse(normalized)
  } catch (error) {
    throw new Error(
      `Planner did not return valid JSON: ${error instanceof Error ? error.message : String(error)}`,
    )
  }

  return voiceoverVideoPlanSchema.parse(parsed)
}
